// alta/AltaTalonario.js
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import { UrlApi } from "../../../../services/apirest";
import { getData } from "../../../../services/library";

const AltaTalonario = ({ open, onClose, tipoId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingUltimo, setLoadingUltimo] = useState(false);
  const [ultimoTalonario, setUltimoTalonario] = useState(null);
  const [sugerencia, setSugerencia] = useState("");
  
  // Obtener la fecha actual en formato YYYY-MM-DD
  const getFechaActual = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Función para formatear punto de venta a 5 dígitos
  const formatearPuntoVenta = (valor) => {
    // Eliminar cualquier carácter que no sea número
    const numeros = valor.replace(/\D/g, '');
    // Limitar a 5 dígitos
    const limitado = numeros.slice(0, 5);
    // Rellenar con ceros a la izquierda si tiene menos de 5 dígitos
    return limitado.padStart(5, '0');
  };

  const [formData, setFormData] = useState({
    id_tipo_talonario: tipoId || "",
    username: "",
    pto_venta: "",
    numero_desde: "",
    numero_hasta: "",
    fecha_asignado: getFechaActual(),
    activo: 1,
  });
  const [tipos, setTipos] = useState([]);

  const obtenerUsers = () => {
    setLoadingUsers(true);
    getData("/choferes/xusernamechoferes", (data) => {
      setUsuarios(data);
      setLoadingUsers(false);
    });
  };

  // Función para obtener el último talonario por tipo y punto de venta
  const obtenerUltimoTalonario = async () => {
    const { id_tipo_talonario, pto_venta } = formData;
    
    if (!id_tipo_talonario || !pto_venta) {
      return;
    }

    setLoadingUltimo(true);
    setError("");
    setSuccess("");

    try {
      // Formatear el punto de venta para la consulta
      const ptoVentaFormateado = formatearPuntoVenta(pto_venta);
      
      const response = await fetch(
        `${UrlApi}/envasado_talonarios/ultimo/${id_tipo_talonario}/${ptoVentaFormateado}`
      );
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.ultimoTalonario) {
        setUltimoTalonario(data.ultimoTalonario);
        const proximoDesde = data.proximoNumeroDesde;
        setFormData(prev => ({
          ...prev,
          numero_desde: proximoDesde.toString(),
          numero_hasta: (proximoDesde + 99).toString()
        }));
        setSugerencia(data.sugerencia);
        setSuccess(`Último talonario encontrado. ${data.sugerencia}`);
      } else {
        setUltimoTalonario(null);
        setSugerencia("");
        setSuccess("No hay talonarios previos. Puedes comenzar desde el número 1.");
        setFormData(prev => ({
          ...prev,
          numero_desde: "1",
          numero_hasta: "100"
        }));
      }
    } catch (error) {
      console.error("Error al obtener último talonario:", error);
      setError("Error al verificar talonarios existentes");
    } finally {
      setLoadingUltimo(false);
    }
  };

  // Efecto para obtener el último talonario cuando cambian tipo o punto de venta
  useEffect(() => {
    if (open && formData.id_tipo_talonario && formData.pto_venta) {
      const delayDebounce = setTimeout(() => {
        obtenerUltimoTalonario();
      }, 500);

      return () => clearTimeout(delayDebounce);
    }
  }, [formData.id_tipo_talonario, formData.pto_venta]);

  const handleUserChange = (event, value) => {
    setFormData((prev) => ({
      ...prev,
      username: value ? value.username : "",
    }));
    setError("");
  };

  useEffect(() => {
    if (open) {
      obtenerTipos();
      obtenerUsers();
      if (tipoId) {
        setFormData((prev) => ({ ...prev, id_tipo_talonario: tipoId }));
      }
      // Resetear estados
      setUltimoTalonario(null);
      setSugerencia("");
      setError("");
      setSuccess("");
      // Resetear fecha al abrir
      setFormData(prev => ({
        ...prev,
        fecha_asignado: getFechaActual()
      }));
    }
  }, [open, tipoId]);

  const obtenerTipos = async () => {
    try {
      const response = await fetch(`${UrlApi}/envasado_tipos_talonario`);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setTipos(data.filter((t) => t.activo === 1));
    } catch (error) {
      console.error("Error al cargar tipos:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si es el campo punto de venta, formatear automáticamente
    if (name === "pto_venta") {
      // Solo permitir números
      const soloNumeros = value.replace(/\D/g, '');
      // Limitar a 5 dígitos
      const limitado = soloNumeros.slice(0, 5);
      // Mostrar sin relleno de ceros mientras el usuario escribe
      setFormData((prev) => ({ ...prev, [name]: limitado }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    setError("");
    setSuccess("");
  };

  // Función para validar punto de venta
  const validarPuntoVenta = (ptoVenta) => {
    const numeros = ptoVenta.replace(/\D/g, '');
    return numeros.length === 5;
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!formData.id_tipo_talonario) {
      setError("Selecciona un tipo de talonario");
      return;
    }
    if (!formData.username) {
      setError("Selecciona un usuario");
      return;
    }
    if (!formData.pto_venta) {
      setError("Ingresa un punto de venta");
      return;
    }
    
    // Validar que el punto de venta tenga 5 dígitos
    if (!validarPuntoVenta(formData.pto_venta)) {
      setError("El punto de venta debe tener 5 dígitos (ejemplo: 00001, 00099, 01000)");
      return;
    }
    
    if (!formData.numero_desde || !formData.numero_hasta) {
      setError("Ingresa los números desde y hasta");
      return;
    }
    if (parseInt(formData.numero_desde) > parseInt(formData.numero_hasta)) {
      setError('El número "desde" debe ser menor que el "hasta"');
      return;
    }

    // Formatear punto de venta a 5 dígitos para guardar
    const ptoVentaFormateado = formatearPuntoVenta(formData.pto_venta);

    // Verificar que el rango no esté ocupado por ningún usuario
    try {
      const verifyResponse = await fetch(`${UrlApi}/envasado_talonarios/verificar-rango`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          idTipo: formData.id_tipo_talonario,
          ptoVenta: ptoVentaFormateado,
          numeroDesde: parseInt(formData.numero_desde),
          numeroHasta: parseInt(formData.numero_hasta)
        })
      });

      const verifyData = await verifyResponse.json();
      
      if (!verifyData.success) {
        if (verifyData.conflictos && verifyData.conflictos.length > 0) {
          const conflictosMsg = verifyData.conflictos.map(c => 
            `Usuario ${c.username}: ${c.desde} - ${c.hasta}`
          ).join('\n');
          setError(`${verifyData.mensaje}\nConflictos:\n${conflictosMsg}`);
        } else {
          setError(verifyData.mensaje || "El rango de números ya está ocupado");
        }
        return;
      }
    } catch (error) {
      console.error("Error al verificar rango:", error);
      setError("Error al verificar disponibilidad del rango");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // Enviar el punto de venta ya formateado
      const dataToSend = {
        ...formData,
        pto_venta: ptoVentaFormateado,
        fecha_asignado: formData.fecha_asignado
      };

      const response = await fetch(`${UrlApi}/envasado_talonarios`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      onClose(true);
    } catch (error) {
      console.error("Error al crear talonario:", error);
      setError(error.message || "Error al crear el talonario");
    } finally {
      setLoading(false);
    }
  };

  // Obtener el punto de venta formateado para mostrar
  const getPuntoVentaDisplay = () => {
    if (!formData.pto_venta) return "";
    return formatearPuntoVenta(formData.pto_venta);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <Box justifyContent="center" backgroundColor="#1F618D">
        <DialogTitle>
          <Typography fontSize={20} align="center" color="#ffffff">
            Nuevo Talonario
          </Typography>
        </DialogTitle>
      </Box>
      <DialogContent>
        {ultimoTalonario && (
          <Alert severity="success" sx={{ mt: 0.5 }}>
            <Typography variant="body2" fontWeight="bold">
              Último talonario cargado para este tipo y punto de venta:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography>
                Usuario: {ultimoTalonario.username} - Desde: {ultimoTalonario.numero_completo_desde} - Hasta: {ultimoTalonario.numero_completo_hasta}
              </Typography>
            </Box>
          </Alert>
        )}

        {loadingUltimo && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Verificando talonarios existentes...
            </Typography>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Talonario</InputLabel>
              <Select
                name="id_tipo_talonario"
                value={formData.id_tipo_talonario}
                onChange={handleChange}
                label="Tipo de Talonario"
                disabled={!!tipoId}
                size="small"
              >
                {tipos.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    {tipo.descripcion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              options={usuarios}
              getOptionLabel={(option) => option.username || ""}
              value={
                usuarios.find((u) => u.username === formData.username) || null
              }
              onChange={handleUserChange}
              loading={loadingUsers}
              loadingText="Cargando usuarios..."
              noOptionsText="No hay usuarios disponibles"
              size="small"
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Usuario"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography variant="body2">{option.username}</Typography>
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Punto de Venta"
              name="pto_venta"
              value={formData.pto_venta}
              onChange={handleChange}
              required
              size="small"
              helperText={
                formData.pto_venta 
                  ? `Formato: ${getPuntoVentaDisplay()} (5 dígitos requeridos)` 
                  : "Ingresa 5 dígitos (ejemplo: 00001, 00099, 01000)"
              }
              error={formData.pto_venta.length > 0 && formData.pto_venta.length < 5}
              InputProps={{
                inputProps: { 
                  maxLength: 5,
                  pattern: '[0-9]*'
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Número Desde"
              name="numero_desde"
              type="number"
              value={formData.numero_desde}
              onChange={handleChange}
              required
              size="small"
              helperText={sugerencia || "Ingresa el número inicial"}
              InputProps={{
                inputProps: { min: 1 }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Número Hasta"
              name="numero_hasta"
              type="number"
              value={formData.numero_hasta}
              onChange={handleChange}
              required
              size="small"
              helperText="Debe ser mayor que el número desde"
              InputProps={{
                inputProps: { min: 1 }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Fecha Asignación"
              name="fecha_asignado"
              type="date"
              value={formData.fecha_asignado}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              size="small"
              helperText="Selecciona la fecha de asignación"
            />
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
                {error}
              </Alert>
            </Grid>
          )}
        </Grid>
        <Grid container direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Grid>
            <Button
              color="error"
              variant="outlined"
              onClick={() => onClose(false)}
              style={{ backgroundColor: "#fff" }}
              disabled={loading}
            >
              Volver
            </Button>
          </Grid>
          <Grid>
            <Button
              onClick={handleSubmit}
              type="submit"
              color="success"
              variant="contained"
              disabled={loading || loadingUltimo}
            >
              {loading ? <CircularProgress size={24} /> : "Guardar"}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AltaTalonario;